import { useState } from 'react'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { type ColumnDef, type OnChangeFn, type SortingState } from '@tanstack/react-table'
import { AlignJustify, Bookmark, Eye, EyeOff, Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { EmptyState } from '@/components/ui/empty-state'
import { PhoneLink } from '@/components/ui/phone-link'
import { AccountTypeBadge } from '@/components/accounts/account-badge'
import { AccountFilters } from '@/components/accounts/account-filters'
import { AccountForm } from '@/components/accounts/account-form'
import { SaveViewDialog } from '@/components/shared/save-view-dialog'
import { ViewTabs } from '@/components/shared/view-tabs'
import { PageHeaderActions } from '@/components/layout/page-header'
import { useAccountsList, type AccountScope } from '@/hooks/use-accounts'
import { useUIStore } from '@/stores/ui-store'
import { cn, relativeTime } from '@/lib/utils'
import { DEFAULT_PAGE_SIZE, pageCount } from '@/lib/list-query'

export const Route = createFileRoute('/_authed/accounts/')({
  component: AccountsIndex,
})

type AccountRow = NonNullable<ReturnType<typeof useAccountsList>['data']>['rows'][number]

const SUB_TYPE_LABELS: Record<string, string> = {
  direct: 'Direct',
  through_distribution: 'Thru Dist.',
  both: 'Both',
}

const ACCOUNT_SCOPES = ['channel', 'end_users', 'all'] as const
const SCOPE_LABELS: Record<AccountScope, string> = {
  channel: 'Channel',
  end_users: 'End Users',
  all: 'All',
}

function AccountsIndex() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = useSearch({ from: '/_authed/accounts' })
  const [formOpen, setFormOpen] = useState(false)
  const [saveOpen, setSaveOpen] = useState(false)
  const compactRows = useUIStore((s) => s.compactRows)
  const toggleCompactRows = useUIStore((s) => s.toggleCompactRows)

  const showInactive = !!search.show_inactive
  const [pageIndex, setPageIndex] = useState(0)

  // Any filter/sort change invalidates the current page position.
  const filterKey = JSON.stringify([
    search.scope, search.type, search.sub_type, search.state, search.country,
    search.search, showInactive, search.sort_by, search.sort_dir,
  ])
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey)
    setPageIndex(0)
  }

  // Filters, sort, and paging all apply server-side.
  const { data, isLoading } = useAccountsList({
    pageIndex,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: search.sort_by,
    sortDir: search.sort_dir,
    showInactive,
    scope: search.scope,
    type: search.type,
    subType: search.sub_type,
    state: search.state,
    country: search.country,
    search: search.search,
  })
  const accounts = data?.rows ?? []
  const total = data?.total ?? 0

  const sorting: SortingState = [{ id: search.sort_by, desc: search.sort_dir === 'desc' }]
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater
    void navigate({
      to: '/accounts',
      search: (prev) => ({
        ...prev,
        sort_by: next[0]?.id ?? 'name',
        sort_dir: next[0]?.desc ? ('desc' as const) : ('asc' as const),
      }),
    })
  }

  const columns: ColumnDef<AccountRow>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader label="Name" column={column} />,
      cell: ({ row }) => (
        <span className={`font-medium block truncate max-w-[200px] ${!row.original.is_active ? 'text-[color:var(--ink-3)]' : ''}`}>
          {row.original.name}
        </span>
      ),
    },
    {
      accessorKey: 'customer_ref',
      header: 'Customer Ref',
      cell: ({ row }) => (
        <span className="font-mono text-[0.92em] text-[color:var(--ink-3)] tabular-nums [font-feature-settings:'tnum'_1] whitespace-nowrap">{row.original.customer_ref ?? '—'}</span>
      ),
    },
    {
      accessorKey: 'type',
      header: ({ column }) => <SortableHeader label="Type" column={column} />,
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <AccountTypeBadge type={row.original.type} />
          {!row.original.is_active && (
            <Badge
              variant="outline"
              className="text-xs font-normal text-[color:var(--ink-3)]"
            >
              Inactive
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'sub_type',
      header: () => <span className="whitespace-nowrap">Sub-type</span>,
      cell: ({ row }) => {
        const st = row.original.sub_type
        return st
          ? <span className="whitespace-nowrap">{SUB_TYPE_LABELS[st] ?? st}</span>
          : <span className="text-[color:var(--ink-3)]">—</span>
      },
    },
    {
      id: 'divisions',
      header: 'Divs',
      cell: ({ row }) => {
        const divs = (row.original as AccountRow & { account_divisions?: { id: string }[] }).account_divisions
        const count = divs?.length ?? 0
        return count > 0
          ? <span className="text-[13px] tabular-nums [font-feature-settings:'tnum'_1] text-[color:var(--ink)]">{count}</span>
          : <span className="text-[color:var(--ink-3)]">—</span>
      },
    },
    {
      id: 'location',
      header: 'Location',
      cell: ({ row }) => {
        const parts = [row.original.city, row.original.state].filter(Boolean).join(', ')
        return parts
          ? <span className="whitespace-nowrap">{parts}</span>
          : <span className="text-[color:var(--ink-3)]">—</span>
      },
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ row }) => <PhoneLink number={row.original.phone} />,
    },
    {
      accessorKey: 'updated_at',
      header: ({ column }) => <SortableHeader label="Updated" column={column} />,
      cell: ({ row }) => (
        <span
          className="text-[12px] text-[color:var(--ink-3)]"
          title={new Date(row.original.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        >
          {relativeTime(row.original.updated_at)}
        </span>
      ),
    },
  ]

  const hasActiveFilters = !!(
    search.type || search.sub_type ||
    search.state || search.country || search.search
  )

  // Filter values only — view/sort/show_inactive are not part of a saved view's filters
  const filterOnly = {
    scope: search.scope,
    type: search.type,
    sub_type: search.sub_type,
    state: search.state,
    country: search.country,
    search: search.search,
  }

  const handleApplyView = (viewId: string | undefined, filters: Record<string, unknown>) => {
    void navigate({
      to: '/accounts',
      search: () => ({
        sort_by: 'name',
        sort_dir: 'asc' as const,
        ...(filters as Partial<typeof search>),
        view: viewId,
      }),
    })
  }

  return (
    <div className="flex flex-col h-full">
      {/* Slice 1B-1: actions live in the ONE shared header via the portal
          slot — no route-owned header bar, title, ThemeToggle, or UserMenu. */}
      <PageHeaderActions>
        <Badge variant="secondary">{isLoading ? '…' : total}</Badge>
        {/* Below lg the header row cannot hold full labels (audit 1B-1
            RETURN: clipped controls at 768) — collapse to icons with stable
            accessible names. The scope toggle lives in the filter row below:
            it narrows the dataset, and the header row (which shares space
            with the 232px sidebar at md) cannot fit a three-segment control
            at 768 on any variant tried. */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button
            variant={showInactive ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={showInactive}
            aria-label={showInactive ? 'Hide inactive' : 'Show inactive'}
            onClick={() =>
              void navigate({
                to: '/accounts',
                search: (prev) => ({
                  ...prev,
                  show_inactive: showInactive ? undefined : true,
                }),
              })
            }
          >
            {showInactive ? (
              <EyeOff className="h-4 w-4 lg:mr-1" />
            ) : (
              <Eye className="h-4 w-4 lg:mr-1" />
            )}
            <span className="hidden lg:inline">
              {showInactive ? 'Hide inactive' : 'Show inactive'}
            </span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label="Save view"
            onClick={() => setSaveOpen(true)}
          >
            <Bookmark className="h-4 w-4 lg:mr-1" />
            <span className="hidden lg:inline">Save view</span>
          </Button>
          <Button size="sm" aria-label="New account" onClick={() => setFormOpen(true)}>
            <Plus className="h-4 w-4 lg:mr-1" />
            <span className="hidden lg:inline">New account</span>
          </Button>
        </div>
      </PageHeaderActions>

      <ViewTabs scope="accounts" activeViewId={search.view} onSelect={handleApplyView} />

      <div className="px-6 h-10 border-b flex items-center gap-2 overflow-x-auto [mask-image:linear-gradient(to_right,black_calc(100%-20px),transparent)] lg:overflow-visible lg:[mask-image:none]">
        <div className="flex shrink-0 items-center rounded-md border border-[color:var(--line)] p-0.5">
          {ACCOUNT_SCOPES.map((scope) => (
            <Button
              key={scope}
              variant="ghost"
              size="sm"
              aria-pressed={search.scope === scope}
              className={cn(
                'h-6 rounded-[5px] px-2.5 text-[12px]',
                search.scope === scope
                  ? 'bg-[color:var(--surface-2)] text-[color:var(--ink)]'
                  : 'text-[color:var(--ink-3)]',
              )}
              onClick={() =>
                // Clear the type filter: it narrows within a scope, and a
                // leftover pick from the previous scope would contradict it.
                void navigate({
                  to: '/accounts',
                  search: (prev) => ({ ...prev, scope, type: undefined }),
                })
              }
            >
              {SCOPE_LABELS[scope]}
            </Button>
          ))}
        </div>
        <AccountFilters
          values={search}
          scope={search.scope}
          onChange={(next) =>
            void navigate({ to: '/accounts', search: (prev) => ({ ...prev, ...(next as typeof prev) }) })
          }
          onClear={() =>
            // Scope is a view tab, not a filter — clearing filters keeps it.
            void navigate({
              to: '/accounts',
              search: () => ({ scope: search.scope, sort_by: 'name', sort_dir: 'asc' as const }),
            })
          }
        />
        <div className="ml-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleCompactRows}
            aria-label={compactRows ? 'Switch to standard density' : 'Switch to compact density'}
            className={compactRows ? 'text-[color:var(--ink)]' : 'text-[color:var(--ink-3)]'}
          >
            <AlignJustify className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="px-6 pt-3 pb-0">
          <DataTable
            columns={columns}
            data={accounts}
            loading={isLoading}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            pagination={{
              pageIndex,
              pageSize: DEFAULT_PAGE_SIZE,
              pageCount: pageCount(total, DEFAULT_PAGE_SIZE),
              totalCount: total,
              onPageChange: setPageIndex,
            }}
            onRowClick={(row) =>
              void navigate({ to: '/accounts/$accountId', params: { accountId: row.id } })
            }
            emptyState={
              hasActiveFilters ? (
                <EmptyState
                  title="No accounts match these filters"
                  description="Try clearing or adjusting your filters to see more."
                />
              ) : (
                <EmptyState
                  title="No accounts yet"
                  description="Accounts are the companies you sell to and through. Create one to start tracking contacts and deals."
                  action={
                    <Button size="sm" data-testid="empty-new-account" onClick={() => setFormOpen(true)}>
                      <Plus className="mr-1 h-4 w-4" />
                      New account
                    </Button>
                  }
                />
              )
            }
          />
        </div>
      </div>

      <AccountForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={(id) =>
          void navigate({ to: '/accounts/$accountId', params: { accountId: id } })
        }
      />
      <SaveViewDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        scope="accounts"
        filters={filterOnly}
        sortBy={search.sort_by}
        sortDir={search.sort_dir}
      />
    </div>
  )
}
